import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import useDelete from '@/core/composables/api/useDelete'
import { useI18n } from 'vue-i18n'
import { useSnackStore } from '@/stores/snack.store'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { Member } from '@/core/models/member/member'
import type { QuestionGroup } from '@/modules/give-feedback/models/question-group'
import type { SurveyState } from '@/modules/survey/models/survey-state'
import type { SurveyRecurrenceFrequency } from '@/modules/start-survey/composables/use-start-survey'
import type { JournalAnalysis } from '@/core/components/journal-analysis/journal-analysis'

export const useCoachSurvey = () => {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const coachSurveyId = route.params.id as string
  const surveyId = computed(() => route.params.id as string)
  const endpoint = computed(() => `coaching/surveys/${surveyId.value}`)

  const { data: survey, status, load, reload } = useGet<CoachSurvey>(endpoint)
  const { remove: removeSurvey, status: statusDeleteSurvey } = useDelete<string>(
    `coaching/surveys/${coachSurveyId}`,
  )

  watch(
    () => route.params.id,
    async () => {
      await load()
    },
    { immediate: true },
  )

  const deleteSurvey = async () => {
    await removeSurvey({})

    switch (statusDeleteSurvey.value) {
      case ApiRequestStatus.Success:
        await router.push({ name: 'coach-surveys' })
        snackStore.showInfo(t('survey.delete.success'))
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('survey.delete.error'))
        break
    }
  }

  return {
    survey,
    status,
    reload,
    deleteSurvey,
  }
}

export interface CoachSurvey {
  readonly id: string
  readonly title?: string
  readonly message?: JSON
  readonly feedbackTopic: {
    id: string
    title: string
    icon?: string
    questionGroups: ReadonlyArray<QuestionGroup>
  }
  readonly surveyTemplateMembers: ReadonlyArray<Member>
  readonly isRecurring: boolean
  readonly recurrence?: CoachSurveyRecurrenceSettings
  readonly nextOccurrenceAt?: string
  readonly createdAt: string
  readonly status: SurveyState
  readonly runningOccurrence?: CoachSurveyRunningOccurrence
  readonly numberOfSurveys: number
  readonly analysis: JournalAnalysis
}

export interface CoachSurveyRecurrenceSettings {
  readonly frequency: SurveyRecurrenceFrequency
  readonly interval: number
  readonly occurrenceCount: number
  readonly nextOccurrenceAt: string
}

export interface CoachSurveyRunningOccurrence {
  readonly finishedSenders: ReadonlyArray<Member>
  readonly unfinishedSenders: ReadonlyArray<Member>
}
